import { useEffect, useMemo, useState } from 'preact/hooks';
import type { WidgetInstance } from '../layouts/layoutHost';
import type { LeafDto, NodeDto, PlantSchemaResponse, WidgetCatalogResponse } from '../api/types';
import { useAsync } from '../hooks/useAsync';
import { api } from '../api/client';
import { LeafPicker } from './LeafPicker';
import './WidgetConfigEditor.css';

interface Props {
  /** The widget instance to edit. Not mutated in place — the caller receives
   *  the updated shape via <c>onSave</c> and decides how to persist it. */
  instance: WidgetInstance;
  /** Plant currently owning the dashboard. Threaded down to the LeafPicker
   *  so it knows which schema to load; passing it as a prop keeps the
   *  editor decoupled from routing / URL state. */
  plantId: string;
  onSave(next: WidgetInstance): void;
  onClose(): void;
}

/**
 * Minimal JSON-Schema Draft-07 subset the editor understands. We do not
 * import a schema validator — the constraints we care about (type, enum,
 * min/max, required) map directly to input attributes, and unknown fields
 * are ignored rather than failing loud. The server is authoritative for
 * validation anyway.
 */
interface JsonSchema {
  type?: string;
  title?: string;
  description?: string;
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
}

interface JsonSchemaProperty {
  type?: string | string[];
  title?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
  format?: string;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  items?: JsonSchemaProperty;
  uniqueItems?: boolean;
  /** Non-standard extension we own: tells the editor to render a specialised
   *  picker instead of a generic input. Currently `"leaf"` triggers the
   *  future LeafPicker (falls back to a plain text field until that lands). */
  'x-hvac-picker'?: string;
}

/**
 * Modal that lets the operator tune a widget's config. Fetches the widget
 * catalog once, looks up the type's configSchema, and renders a form
 * dynamically. Submits a new WidgetInstance via <c>onSave</c>; the
 * DashboardPage decides whether to mark the layout dirty and re-render.
 */
export function WidgetConfigEditor({ instance, plantId, onSave, onClose }: Props) {
  const state = useAsync(
    (signal) => api.get<WidgetCatalogResponse>('/api/widgets/catalog', signal),
    [],
  );
  // Plant schema drives the ID → displayName lookup for all leaf-picker
  // fields — the operator sees leaf names, not raw UUIDs. Load once per
  // dialog open; the picker itself loads the same endpoint independently
  // and browser cache dedupes.
  const schemaState = useAsync(
    (signal) => api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, signal),
    [plantId],
  );
  const leafLookup = useMemo(() => {
    if (schemaState.status !== 'ok') return new Map<string, LeafDto>();
    return buildLeafLookup(schemaState.data);
  }, [schemaState]);
  const [values, setValues] = useState<Record<string, unknown>>({ ...instance.config });
  /** Which config key is currently being edited via the LeafPicker overlay,
   *  or null if the picker is closed. State lives here so the picker can
   *  close cleanly and hand back a leaf id to that key. */
  const [pickerKey, setPickerKey] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const entry = state.status === 'ok'
    ? state.data.widgets.find((w) => w.id === instance.widgetTypeId)
    : undefined;

  const set = (key: string, val: unknown) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const save = () => {
    onSave({ ...instance, config: values });
  };

  return (
    <div class="hv-cfg" role="dialog" aria-modal="true" aria-labelledby="hv-cfg-title">
      <div class="hv-cfg__backdrop" onClick={onClose} />
      <div class="hv-cfg__panel">
        <header class="hv-cfg__header">
          <div>
            <h2 id="hv-cfg-title" class="hv-cfg__title">
              {entry?.displayName ?? 'Widget-Konfiguration'}
            </h2>
            {entry?.description && (
              <p class="hv-cfg__subtitle muted">{entry.description}</p>
            )}
          </div>
          <button
            type="button"
            class="hv-cfg__close"
            onClick={onClose}
            aria-label="Schließen"
            title="Schließen"
          >
            ×
          </button>
        </header>
        <div class="hv-cfg__body">
          {state.status === 'loading' && <p class="muted">Schema wird geladen…</p>}
          {state.status === 'error' && (
            <p class="hv-cfg__error">Widget-Katalog konnte nicht geladen werden.</p>
          )}
          {state.status === 'ok' && !entry && (
            <p class="hv-cfg__error">
              Widget-Typ <code class="mono">{instance.widgetTypeId}</code> ist im Katalog nicht (mehr) enthalten.
            </p>
          )}
          {state.status === 'ok' && entry && (
            <SchemaForm
              schema={entry.configSchema as JsonSchema}
              values={values}
              leafLookup={leafLookup}
              onChange={set}
              onOpenLeafPicker={(key) => setPickerKey(key)}
            />
          )}
        </div>
        <footer class="hv-cfg__footer">
          <button
            type="button"
            class="hv-btn hv-btn--secondary hv-btn--sm"
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            type="button"
            class={`hv-btn hv-btn--primary hv-btn--sm${state.status !== 'ok' || !entry ? ' hv-btn--disabled' : ''}`}
            onClick={save}
            disabled={state.status !== 'ok' || !entry}
          >
            Speichern
          </button>
        </footer>
      </div>
      {pickerKey && (() => {
        // Composite picker keys "arrayField:index:leafField" address a single
        // row's leaf inside an array-of-objects property (currently used by
        // the energy-flow flows[] editor). We split once, resolve the current
        // value from the row, and route the onSelect back into the row.
        const composite = pickerKey.includes(':')
          ? (() => {
            const parts = pickerKey.split(':');
            return { parent: parts[0], index: Number(parts[1]), field: parts[2] };
          })()
          : null;
        const currentLeafId = composite
          ? (() => {
            const arr = Array.isArray(values[composite.parent]) ? values[composite.parent] as Record<string, unknown>[] : [];
            const row = arr[composite.index];
            return row && typeof row[composite.field] === 'string' ? (row[composite.field] as string) : null;
          })()
          : (typeof values[pickerKey] === 'string' ? (values[pickerKey] as string) : null);
        return (
          <LeafPicker
            plantId={plantId}
            currentLeafId={currentLeafId}
            onSelect={(leaf: LeafDto) => {
              if (composite) {
                const arr = (Array.isArray(values[composite.parent])
                  ? values[composite.parent] as Record<string, unknown>[]
                  : []).slice();
                const row = { ...(arr[composite.index] ?? {}) };
                row[composite.field] = leaf.id;
                arr[composite.index] = row;
                set(composite.parent, arr);
                setPickerKey(null);
                return;
              }
              // Multi vs. single is decided by the schema property's picker
              // hint. Multi = append (avoid duplicates); single = replace.
              const prop = entry
                ? (entry.configSchema as JsonSchema)?.properties?.[pickerKey]
                : undefined;
              if (prop?.['x-hvac-picker'] === 'leaf-multi') {
                const raw = values[pickerKey];
                const arr: string[] = Array.isArray(raw) ? (raw as string[]) : [];
                if (!arr.includes(leaf.id)) {
                  set(pickerKey, [...arr, leaf.id]);
                }
              } else {
                set(pickerKey, leaf.id);
              }
              setPickerKey(null);
            }}
            onClose={() => setPickerKey(null)}
          />
        );
      })()}
    </div>
  );
}

function SchemaForm({
  schema, values, leafLookup, onChange, onOpenLeafPicker,
}: {
  schema: JsonSchema;
  values: Record<string, unknown>;
  leafLookup: Map<string, LeafDto>;
  onChange(key: string, val: unknown): void;
  onOpenLeafPicker(key: string): void;
}) {
  const props = schema.properties ?? {};
  const required = new Set(schema.required ?? []);
  const keys = Object.keys(props);
  if (keys.length === 0) {
    return <p class="muted">Keine Konfigurationsfelder.</p>;
  }
  return (
    <div class="hv-cfg__fields">
      {keys.map((key) => (
        <SchemaField
          key={key}
          fieldKey={key}
          prop={props[key]}
          value={values[key]}
          required={required.has(key)}
          leafLookup={leafLookup}
          onChange={(v) => onChange(key, v)}
          onOpenLeafPicker={(compositeKey) => onOpenLeafPicker(compositeKey ?? key)}
        />
      ))}
    </div>
  );
}

function SchemaField({
  fieldKey, prop, value, required, leafLookup, onChange, onOpenLeafPicker,
}: {
  fieldKey: string;
  prop: JsonSchemaProperty;
  value: unknown;
  required: boolean;
  leafLookup: Map<string, LeafDto>;
  onChange(v: unknown): void;
  onOpenLeafPicker(compositeKey?: string): void;
}) {
  const label = prop.title ?? fieldKey;
  const help = prop.description;
  const t = Array.isArray(prop.type) ? prop.type[0] : prop.type;

  return (
    <div class="hv-cfg__field">
      <label class="hv-cfg__label">
        {label}
        {required && <span class="hv-cfg__required" aria-hidden="true">*</span>}
      </label>
      {renderInput(fieldKey, prop, t, value, leafLookup, onChange, onOpenLeafPicker)}
      {help && <div class="hv-cfg__help">{help}</div>}
    </div>
  );
}

function renderInput(
  key: string,
  prop: JsonSchemaProperty,
  type: string | undefined,
  value: unknown,
  leafLookup: Map<string, LeafDto>,
  onChange: (v: unknown) => void,
  onOpenLeafPicker: (compositeKey?: string) => void,
) {
  const leafLabelFor = (leafId: string): string => {
    const leaf = leafLookup.get(leafId);
    if (!leaf) return `${leafId.slice(0, 8)}…`;
    return leaf.displayName ?? leaf.symbol ?? leafId;
  };
  // 1. Enum → dropdown (works for string enums; numeric enums coerce back).
  if (Array.isArray(prop.enum) && prop.enum.length > 0) {
    const current = value ?? prop.default ?? prop.enum[0];
    return (
      <select
        class="hv-cfg__input"
        value={String(current)}
        onChange={(e) => {
          const raw = (e.currentTarget as HTMLSelectElement).value;
          // Coerce back to the enum's original type by finding the match.
          const matched = prop.enum!.find((v) => String(v) === raw) ?? raw;
          onChange(matched);
        }}
      >
        {prop.enum.map((v) => (
          <option key={String(v)} value={String(v)}>{String(v)}</option>
        ))}
      </select>
    );
  }

  // 2. Boolean → checkbox
  if (type === 'boolean') {
    const checked = value === true || (value === undefined && prop.default === true);
    return (
      <label class="hv-cfg__inline-check">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange((e.currentTarget as HTMLInputElement).checked)}
        />
        <span class="muted">{checked ? 'aktiv' : 'aus'}</span>
      </label>
    );
  }

  // 3. Number / integer → number input with min/max/step from the schema.
  if (type === 'integer' || type === 'number') {
    const num = typeof value === 'number' ? value : prop.default;
    return (
      <input
        class="hv-cfg__input"
        type="number"
        value={num as number | string | undefined ?? ''}
        min={prop.minimum}
        max={prop.maximum}
        step={type === 'integer' ? 1 : 'any'}
        onInput={(e) => {
          const raw = (e.currentTarget as HTMLInputElement).value;
          if (raw === '') { onChange(undefined); return; }
          const n = type === 'integer' ? parseInt(raw, 10) : parseFloat(raw);
          onChange(Number.isFinite(n) ? n : undefined);
        }}
      />
    );
  }

  // 3a. Energy-Flow flows editor. Array-of-objects with per-row fromLabel,
  // toLabel, leafId and unit. Each row has its own LeafPicker button (routed
  // through a composite picker-key), free-text From/To inputs, an inline
  // unit field, and × to remove. The + button appends an empty row so the
  // user can build up the topology incrementally.
  if (prop['x-hvac-picker'] === 'energy-flows') {
    const raw = value ?? prop.default ?? [];
    const arr: Record<string, unknown>[] = Array.isArray(raw) ? raw as Record<string, unknown>[] : [];
    const patch = (i: number, field: string, val: unknown) => {
      const next = arr.slice();
      next[i] = { ...(next[i] ?? {}), [field]: val };
      onChange(next);
    };
    const remove = (i: number) => onChange(arr.filter((_, k) => k !== i));
    const move = (i: number, dir: -1 | 1) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return;
      const next = arr.slice();
      [next[i], next[j]] = [next[j], next[i]];
      onChange(next);
    };
    const add = () => onChange([...arr, { fromLabel: '', toLabel: '', leafId: '', unit: '' }]);
    return (
      <div class="hv-cfg__flows">
        {arr.length === 0 && (
          <div class="hv-cfg__flows-empty muted">Noch keine Flüsse. Unten hinzufügen.</div>
        )}
        {arr.map((row, i) => {
          const leafId = typeof row.leafId === 'string' ? row.leafId : '';
          return (
            <div key={i} class="hv-cfg__flows-row">
              <div class="hv-cfg__flows-fields">
                <label class="hv-cfg__flows-cell">
                  <span class="hv-cfg__flows-cell-label muted">Von</span>
                  <input
                    class="hv-cfg__input hv-cfg__flows-input"
                    type="text"
                    placeholder="z. B. Solar"
                    value={String(row.fromLabel ?? '')}
                    onInput={(e) => patch(i, 'fromLabel', (e.currentTarget as HTMLInputElement).value)}
                  />
                </label>
                <label class="hv-cfg__flows-cell">
                  <span class="hv-cfg__flows-cell-label muted">Nach</span>
                  <input
                    class="hv-cfg__input hv-cfg__flows-input"
                    type="text"
                    placeholder="z. B. Batterie"
                    value={String(row.toLabel ?? '')}
                    onInput={(e) => patch(i, 'toLabel', (e.currentTarget as HTMLInputElement).value)}
                  />
                </label>
                <label class="hv-cfg__flows-cell hv-cfg__flows-cell--wide">
                  <span class="hv-cfg__flows-cell-label muted">Datenpunkt</span>
                  <button
                    type="button"
                    class="hv-cfg__leaf-button"
                    onClick={() => onOpenLeafPicker(`${key}:${i}:leafId`)}
                  >
                    {leafId
                      ? <span class="hv-cfg__leaf-value" title={leafId}>{leafLabelFor(leafId)}</span>
                      : <span class="muted">Datenpunkt wählen…</span>}
                    <span class="hv-cfg__leaf-chevron" aria-hidden="true">▸</span>
                  </button>
                </label>
                <label class="hv-cfg__flows-cell hv-cfg__flows-cell--unit">
                  <span class="hv-cfg__flows-cell-label muted">Einheit</span>
                  <input
                    class="hv-cfg__input hv-cfg__flows-input"
                    type="text"
                    placeholder="kW"
                    value={String(row.unit ?? '')}
                    onInput={(e) => patch(i, 'unit', (e.currentTarget as HTMLInputElement).value)}
                  />
                </label>
              </div>
              <div class="hv-cfg__flows-actions">
                <button
                  type="button"
                  class="hv-cfg__order-btn"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  aria-label="Nach oben"
                  title="Nach oben"
                >↑</button>
                <button
                  type="button"
                  class="hv-cfg__order-btn"
                  onClick={() => move(i, +1)}
                  disabled={i === arr.length - 1}
                  aria-label="Nach unten"
                  title="Nach unten"
                >↓</button>
                <button
                  type="button"
                  class="hv-cfg__order-btn hv-cfg__order-btn--remove"
                  onClick={() => remove(i)}
                  aria-label="Entfernen"
                  title="Entfernen"
                >×</button>
              </div>
            </div>
          );
        })}
        <button
          type="button"
          class="hv-cfg__leaf-multi-add"
          onClick={add}
        >
          + Fluss hinzufügen
        </button>
      </div>
    );
  }

  // 3b. Array of enum with `x-hvac-picker: "enum-order"` → reorderable list.
  // Shows selected items in the current order, each with ↑/↓ reorder and ×
  // remove. Unselected items appear in a dropdown below to add. Used for
  // column-order editing where the array is BOTH the visibility set and
  // the layout order.
  if (
    type === 'array'
    && prop.items && Array.isArray(prop.items.enum)
    && prop['x-hvac-picker'] === 'enum-order'
  ) {
    const raw = value ?? prop.default ?? [];
    const arr: string[] = Array.isArray(raw) ? (raw as string[]).slice() : [];
    const all = prop.items.enum as string[];
    const available = all.filter((v) => !arr.includes(v));
    const move = (i: number, dir: -1 | 1) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return;
      const next = arr.slice();
      [next[i], next[j]] = [next[j], next[i]];
      onChange(next);
    };
    const remove = (i: number) => onChange(arr.filter((_, k) => k !== i));
    const add = (v: string) => onChange([...arr, v]);
    return (
      <div class="hv-cfg__order-list">
        {arr.length === 0 && (
          <div class="hv-cfg__order-empty muted">Keine Einträge sichtbar. Unten hinzufügen.</div>
        )}
        {arr.map((item, i) => (
          <div key={item} class="hv-cfg__order-row">
            <span class="hv-cfg__order-label">{item}</span>
            <div class="hv-cfg__order-actions">
              <button
                type="button"
                class="hv-cfg__order-btn"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="Nach oben"
                title="Nach oben"
              >↑</button>
              <button
                type="button"
                class="hv-cfg__order-btn"
                onClick={() => move(i, +1)}
                disabled={i === arr.length - 1}
                aria-label="Nach unten"
                title="Nach unten"
              >↓</button>
              <button
                type="button"
                class="hv-cfg__order-btn hv-cfg__order-btn--remove"
                onClick={() => remove(i)}
                aria-label="Entfernen"
                title="Entfernen"
              >×</button>
            </div>
          </div>
        ))}
        {available.length > 0 && (
          <select
            class="hv-cfg__order-add"
            value=""
            onChange={(e) => {
              const v = (e.currentTarget as HTMLSelectElement).value;
              if (v) add(v);
              (e.currentTarget as HTMLSelectElement).value = '';
            }}
          >
            <option value="">+ Spalte hinzufügen…</option>
            {available.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
      </div>
    );
  }

  // 4. Array of enum → multi-checkbox group.
  if (type === 'array' && prop.items && Array.isArray(prop.items.enum)) {
    const raw = value ?? prop.default ?? [];
    const arr: unknown[] = Array.isArray(raw) ? raw : [];
    const selected = new Set(arr);
    return (
      <div class="hv-cfg__checkgroup">
        {(prop.items.enum as unknown[]).map((opt) => (
          <label key={String(opt)} class="hv-cfg__check-item">
            <input
              type="checkbox"
              checked={selected.has(opt)}
              onChange={(e) => {
                const next = new Set(selected);
                if ((e.currentTarget as HTMLInputElement).checked) next.add(opt);
                else next.delete(opt);
                onChange(Array.from(next));
              }}
            />
            <span>{String(opt)}</span>
          </label>
        ))}
      </div>
    );
  }

  // 5a. Multi-leaf-picker. When `x-hvac-picker: "leaf-multi"` is set on
  // an array-of-strings property, the field renders as a vertical list of
  // rows — one row per selected leaf — each with ↑/↓ reorder and × remove
  // buttons, plus a "+ Datenpunkt" button below that opens the LeafPicker
  // to append another. The array order is user-controlled (drives legend /
  // table row order downstream); duplicates are still prevented in the
  // LeafPicker onSelect handler (see the modal above).
  if (prop['x-hvac-picker'] === 'leaf-multi') {
    const raw = value ?? prop.default;
    const arr: string[] = Array.isArray(raw) ? (raw as string[]) : [];
    const move = (i: number, dir: -1 | 1) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return;
      const next = arr.slice();
      [next[i], next[j]] = [next[j], next[i]];
      onChange(next);
    };
    const remove = (i: number) => onChange(arr.filter((_, k) => k !== i));
    return (
      <div class="hv-cfg__leaf-multi">
        {arr.length === 0 && (
          <div class="hv-cfg__leaf-multi-empty muted">Noch kein Datenpunkt gewählt.</div>
        )}
        {arr.length > 0 && (
          <div class="hv-cfg__leaf-multi-list">
            {arr.map((id, i) => (
              <div key={id} class="hv-cfg__leaf-multi-row">
                <span class="hv-cfg__leaf-multi-label" title={id}>{leafLabelFor(id)}</span>
                <div class="hv-cfg__leaf-multi-actions">
                  <button
                    type="button"
                    class="hv-cfg__leaf-multi-btn"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label="Nach oben"
                    title="Nach oben"
                  >↑</button>
                  <button
                    type="button"
                    class="hv-cfg__leaf-multi-btn"
                    onClick={() => move(i, +1)}
                    disabled={i === arr.length - 1}
                    aria-label="Nach unten"
                    title="Nach unten"
                  >↓</button>
                  <button
                    type="button"
                    class="hv-cfg__leaf-multi-btn hv-cfg__leaf-multi-btn--remove"
                    onClick={() => remove(i)}
                    aria-label="Entfernen"
                    title="Entfernen"
                  >×</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          class="hv-cfg__leaf-multi-add"
          onClick={() => onOpenLeafPicker()}
        >
          + Datenpunkt hinzufügen
        </button>
      </div>
    );
  }

  // 5. Leaf-picker. The `x-hvac-picker: "leaf"` extension on a schema
  // property (also recognised for the widely-used `leafId` key with
  // `format: uuid` as a fallback) opens the LeafPicker overlay. The button
  // itself shows the currently-selected id — the picker resolves the label
  // when it opens. Empty state prompts explicitly.
  const isLeafField = prop['x-hvac-picker'] === 'leaf'
    || (key === 'leafId' && prop.format === 'uuid');
  if (isLeafField) {
    const current = value ?? prop.default;
    const currentStr = typeof current === 'string' && current ? current : '';
    return (
      <div class="hv-cfg__leaf-field">
        <button
          type="button"
          class="hv-cfg__leaf-button"
          onClick={() => onOpenLeafPicker()}
        >
          {currentStr
            ? <span class="hv-cfg__leaf-value" title={currentStr}>{leafLabelFor(currentStr)}</span>
            : <span class="muted">Datenpunkt wählen…</span>}
          <span class="hv-cfg__leaf-chevron" aria-hidden="true">▸</span>
        </button>
        {currentStr && (
          <button
            type="button"
            class="hv-cfg__leaf-clear"
            onClick={() => onChange('')}
            aria-label="Auswahl entfernen"
            title="Auswahl entfernen"
          >
            ×
          </button>
        )}
      </div>
    );
  }

  // 6. Multiline: prose-style fields get a textarea. Convention over
  // configuration — the property name is the signal. Keeps the JSON-Schema
  // small; a full `format: "textarea"` would require carrying it through
  // every widget catalog entry.
  const isMultiline = key === 'content' || key === 'description'
    || key === 'notes' || key === 'body';
  if (type === 'string' && isMultiline) {
    return (
      <textarea
        class="hv-cfg__textarea"
        rows={6}
        maxLength={prop.maxLength}
        value={String(value ?? prop.default ?? '')}
        onInput={(e) => onChange((e.currentTarget as HTMLTextAreaElement).value)}
      />
    );
  }

  // 7. Default: single-line text input. `format: uri` → type="url" for the
  // native URL validator; everything else stays plain text.
  return (
    <input
      class="hv-cfg__input"
      type={prop.format === 'uri' ? 'url' : 'text'}
      value={String(value ?? prop.default ?? '')}
      minLength={prop.minLength}
      maxLength={prop.maxLength}
      onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
    />
  );
}

/** Walk the plant schema once and build an ID → leaf map so the editor can
 *  resolve any configured leafId to its displayName / symbol in O(1). */
function buildLeafLookup(schema: PlantSchemaResponse): Map<string, LeafDto> {
  const map = new Map<string, LeafDto>();
  const walk = (nodes: NodeDto[]) => {
    for (const node of nodes) {
      for (const leaf of node.leaves) map.set(leaf.id, leaf);
      walk(node.children);
    }
  };
  for (const device of schema.devices) walk(device.nodes);
  return map;
}
